import { useEffect, useState } from 'react';
import { createRoute } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import { useInventory } from '../../hooks/useInventories';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { EditSkeleton } from '../../components/Skeleton/EditSkeleton';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CAPABILITIES } from '../../lib/capabilities';
import { requireCapability } from '../requireCapability';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type InventoryManagement from '../../components/InventoryManagement/index';
import type InventoryDetails from '../../components/InventoryDetails/index';
import type EditInventory from '../../components/Inventory/EditInventory/index';
import type { InventoryTranslations } from '../../components/Inventory/types';

export const inventoriesIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventories',
  component: function InventoriesIndexRoute() {
    const { initialData, translations, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    // First render renders the skeleton so React hydration byte-matches the
    // Phlex skeleton; the real component is dynamic-imported in an effect so
    // it stays in its own chunk.
    useDocumentTitle(t('common.page_titles.inventories'));
    const [Component, setComponent] = useState<typeof InventoryManagement | null>(null);
    useEffect(() => {
      import('../../components/InventoryManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_inventory')} />;
    }
    return (
      <Component
        initialData={initialData}
        translations={translations as InventoryTranslations | undefined}
        currentUser={currentUser}
      />
    );
  },
});

export const inventoryDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventories/$id/details',
  component: function InventoryDetailsRoute() {
    const params = inventoryDetailsRoute.useParams();
    const { initialInventory, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed = initialInventory && initialInventory.id === idNum ? initialInventory : undefined;
    const { data: inventoryData } = useInventory(idNum, {
      initialData: seed ? { inventory: seed } : undefined,
    });
    useDocumentTitle(inventoryData?.inventory?.name ?? t('common.page_titles.inventories'));
    const [Component, setComponent] = useState<typeof InventoryDetails | null>(null);
    useEffect(() => {
      import('../../components/InventoryDetails/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <DetailsSkeleton label={t('common.loading_inventory')} />;
    }
    return <Component inventoryId={idNum} initialInventory={seed} currentUser={currentUser} />;
  },
});

export const inventoryEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inventories/$id/edit',
  // RBAC: the edit page is a write-only surface. Read-only users (no
  // inventories.write) are redirected to the read-only details page before the
  // component or its loader runs. The server policy enforces this too.
  beforeLoad: requireCapability(CAPABILITIES.INVENTORIES_WRITE, ({ params }) => ({
    to: '/inventories/$id/details',
    params: { id: params.id },
  })),
  component: function InventoryEditRoute() {
    const params = inventoryEditRoute.useParams();
    const { initialInventory, editOptions } = useAppRouterInitial();
    const { t } = useTranslation();
    const idNum = Number(params.id);
    const seed = initialInventory && initialInventory.id === idNum ? initialInventory : undefined;
    // In-router nav from /inventories arrives without an initialInventory seed;
    // fetch so EditInventory always receives a defined inventory.
    const { data } = useInventory(idNum, {
      initialData: seed ? { inventory: seed } : undefined,
    });
    useDocumentTitle(
      data?.inventory ? t('common.page_titles.edit', { name: data?.inventory.name }) : t('common.page_titles.inventories'),
    );
    const [Component, setComponent] = useState<typeof EditInventory | null>(null);
    useEffect(() => {
      import('../../components/Inventory/EditInventory/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    const inventory = data?.inventory;
    if (!Component || !inventory) {
      return <EditSkeleton label={t('common.loading_inventory')} />;
    }
    return (
      <Component inventory={inventory} options={editOptions ?? { categories: [], locations: [] }} />
    );
  },
});
